'use client';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSetupProfile } from '@/hooks/useSetupProfile';
import { useMemo, useState } from 'react';

interface EnhancedSearchFilters {
  contentType: 'all' | 'questions' | 'stories';
  questionTypes: string[];
  areaOfInterestIds: string[];
  categories: string[]; // Area of Interest names
  industryIds: string[];
  occupationIds: string[];
  tags: string[];
}

interface EnhancedSearchFilterProps {
  selectedFilters: EnhancedSearchFilters;
  setSelectedFilters: (filters: EnhancedSearchFilters) => void;
  availableFilters?: {
    questionTypes: Array<{ key: string; label: string; count: number }>;
    tags: Array<{ key: string; count: number }>;
    roles: Array<{ key: string; count: number }>;
    industries: Array<{ key: string; count: number }>;
  };
  isLoading?: boolean;
}

export const EnhancedSearchFilter = ({
  selectedFilters,
  setSelectedFilters,
  availableFilters,
  isLoading = false,
}: EnhancedSearchFilterProps) => {
  const { getCategoriesApi, getOccupationApi, getIndustryApi } =
    useSetupProfile({
      loadCategories: true,
      loadOccupation: true,
      loadIndustry: true,
    });

  const [openCategories, setOpenCategories] = useState<string[]>([]);

  type OccupationParent = {
    id: string;
    name: string;
    subOccupations: { id: string; name: string }[];
  };

  const sortedCategoryEntries = useMemo(() => {
    const data = getCategoriesApi.data?.data;
    if (!data) return [] as [string, { id: string; name: string }[]][];
    return Object.entries(data)
      .map(
        ([key, value]) =>
          [key, [...value].sort((a, b) => a.name.localeCompare(b.name))] as [
            string,
            { id: string; name: string }[],
          ]
      )
      .sort(([a], [b]) => a.localeCompare(b));
  }, [getCategoriesApi.data]);

  const sortedOccupationParents = useMemo(() => {
    const raw = getOccupationApi.data?.data;
    if (!Array.isArray(raw) || raw.length === 0) return [] as OccupationParent[];
    return [...(raw as OccupationParent[])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((parent) => ({
        ...parent,
        subOccupations: [...parent.subOccupations].sort((x, y) =>
          x.name.localeCompare(y.name)
        ),
      }));
  }, [getOccupationApi.data]);

  const sortedIndustryParents = useMemo(() => {
    const raw = getIndustryApi.data?.data;
    if (!Array.isArray(raw) || raw.length === 0)
      return [] as {
        id: string;
        name: string;
        subIndustries: { id: string; name: string }[];
      }[];
    return [...(raw as { id: string; name: string; subIndustries: { id: string; name: string }[] }[])]
      .filter((parent) => parent.subIndustries.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((parent) => ({
        ...parent,
        subIndustries: [...parent.subIndustries].sort((x, y) =>
          x.name.localeCompare(y.name)
        ),
      }));
  }, [getIndustryApi.data]);

  const questionTypeOptions = [
    { key: 'IDEA_STARTER', label: 'Idea Starter', icon: '💡' },
    { key: 'DISCUSSIONS', label: 'Discussion Forum', icon: '💬' },
    { key: 'NORMAL', label: 'Common Questions', icon: '❓' },
  ];

  const handleContentTypeChange = (type: 'all' | 'questions' | 'stories') => {
    setSelectedFilters({
      ...selectedFilters,
      contentType: type,
      questionTypes: type === 'questions' ? selectedFilters.questionTypes : [],
    });
  };

  const handleQuestionTypeChange = (questionType: string, checked: boolean) => {
    const newQuestionTypes = checked
      ? [...selectedFilters.questionTypes, questionType]
      : selectedFilters.questionTypes.filter((type) => type !== questionType);
    setSelectedFilters({ ...selectedFilters, questionTypes: newQuestionTypes });
  };

  const handleAreaOfInterestChange = (
    filterId: string,
    checked: boolean,
    label?: string
  ) => {
    // 🔍 DEBUG: Log area of interest checkbox changes
    console.log('🔍 CHECKBOX DEBUG - Area of Interest Checkbox Changed:', {
      filterId: filterId,
      checked: checked,
      currentFilters: selectedFilters.areaOfInterestIds,
      filterIdType: typeof filterId,
    });

    const newIds = checked
      ? [...selectedFilters.areaOfInterestIds, filterId]
      : selectedFilters.areaOfInterestIds.filter((id) => id !== filterId);

    // Maintain parallel list of names for name-based filtering
    const newNames = (() => {
      const set = new Set<string>(selectedFilters.categories || []);
      if (checked && label) set.add(label);
      if (!checked && label) set.delete(label);
      return Array.from(set);
    })();

    // 🔍 DEBUG: Log the new filter state
    console.log('🔍 CHECKBOX DEBUG - New Filter State:', {
      newIds,
      newNames,
      allNewFilters: {
        ...selectedFilters,
        areaOfInterestIds: newIds,
        categories: newNames,
      },
    });

    setSelectedFilters({
      ...selectedFilters,
      areaOfInterestIds: newIds,
      categories: newNames,
    });
  };

  const handleOccupationChange = (filterId: string, checked: boolean) => {
    const newFilters = checked
      ? [...selectedFilters.occupationIds, filterId]
      : selectedFilters.occupationIds.filter((id) => id !== filterId);
    setSelectedFilters({ ...selectedFilters, occupationIds: newFilters });
  };

  const handleIndustryChange = (filterId: string, checked: boolean) => {
    const newFilters = checked
      ? [...selectedFilters.industryIds, filterId]
      : selectedFilters.industryIds.filter((id) => id !== filterId);
    setSelectedFilters({ ...selectedFilters, industryIds: newFilters });
  };

  // Handle select all for a category
  const handleSelectAll = (
    categoryItems: { id: string; name?: string }[],
    checked: boolean,
    filterType: 'areaOfInterest' | 'industry' | 'occupation'
  ) => {
    const currentFilters = { ...selectedFilters };
    const targetArray =
      filterType === 'areaOfInterest'
        ? currentFilters.areaOfInterestIds
        : filterType === 'industry'
          ? currentFilters.industryIds
          : currentFilters.occupationIds;

    const newFilters = checked
      ? [...targetArray, ...categoryItems.map((item) => item.id)]
      : targetArray.filter(
          (id) => !categoryItems.some((item) => item.id === id)
        );

    const updatedFilters = {
      ...currentFilters,
      [filterType === 'areaOfInterest'
        ? 'areaOfInterestIds'
        : filterType === 'industry'
          ? 'industryIds'
          : 'occupationIds']: newFilters,
    };

    // Update categories names for area of interest
    if (filterType === 'areaOfInterest') {
      const newNames = (() => {
        const set = new Set<string>(selectedFilters.categories || []);
        categoryItems.forEach((item) => {
          if (checked && item.name) set.add(item.name);
          if (!checked && item.name) set.delete(item.name);
        });
        return Array.from(set);
      })();
      updatedFilters.categories = newNames;
    }

    setSelectedFilters(updatedFilters);
  };

  // Check if all items in a category are selected
  const areAllItemsSelected = (
    categoryItems: { id: string }[],
    filterType: 'areaOfInterest' | 'industry' | 'occupation'
  ) => {
    const targetArray =
      filterType === 'areaOfInterest'
        ? selectedFilters.areaOfInterestIds
        : filterType === 'industry'
          ? selectedFilters.industryIds
          : selectedFilters.occupationIds;

    return categoryItems.every((item) => targetArray.includes(item.id));
  };

  // Check if some items in a category are selected
  const areSomeItemsSelected = (
    categoryItems: { id: string }[],
    filterType: 'areaOfInterest' | 'industry' | 'occupation'
  ) => {
    const targetArray =
      filterType === 'areaOfInterest'
        ? selectedFilters.areaOfInterestIds
        : filterType === 'industry'
          ? selectedFilters.industryIds
          : selectedFilters.occupationIds;
    return categoryItems.some((item) => targetArray.includes(item.id));
  };

  const handleClearAll = () => {
    setSelectedFilters({
      contentType: 'all',
      questionTypes: [],
      areaOfInterestIds: [],
      categories: [],
      industryIds: [],
      occupationIds: [],
      tags: [],
    });
  };

  const activeFilterCount =
    selectedFilters.questionTypes.length +
    selectedFilters.areaOfInterestIds.length +
    selectedFilters.industryIds.length +
    selectedFilters.occupationIds.length +
    (selectedFilters.contentType !== 'all' ? 1 : 0);

  return (
    <aside className="sticky top-20 self-start py-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-normal">Filters</h2>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          className="text-xs hover:bg-ihs-blue/20"
          size="sm"
          onClick={handleClearAll}
        >
          Clear All
        </Button>
      </div>
      <div className="pr-4 max-h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-6 w-full mb-2" />
            ))}
          </div>
        ) : (
          <>
            {/* Content Type - Always visible, not collapsible */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Content Type
              </h3>
              <div className="flex flex-col gap-3 py-2 px-4">
                <Tabs
                  value={selectedFilters.contentType}
                  onValueChange={(value) =>
                    handleContentTypeChange(
                      value as 'all' | 'questions' | 'stories'
                    )
                  }
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="questions">Questions</TabsTrigger>
                    <TabsTrigger value="stories">Stories</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>

            {/* Other filter sections - Collapsible */}
            <Accordion
              type="multiple"
              className="w-full"
              value={openCategories}
              onValueChange={setOpenCategories}
            >
              <AccordionItem className="border-none" value="Areas of Interest">
                <AccordionTrigger
                  isChevron
                  className="text-sm justify-between w-full hover:bg-gray-50 rounded-md px-2 py-1 transition-colors"
                >
                  Areas of Interest
                </AccordionTrigger>
                <AccordionContent>
                  <div className="max-h-[200px] overflow-y-auto w-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                    {sortedCategoryEntries.map(([key, value]) => (
                      <div key={key} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Label
                            htmlFor={`select-all-${key}`}
                            className="text-sm font-medium"
                          >
                            {key}
                          </Label>
                        </div>
                        <div className="flex flex-col gap-2 px-4">
                          {value.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={`area-${item.id}`}
                                checked={selectedFilters.areaOfInterestIds.includes(
                                  item.id
                                )}
                                onCheckedChange={(checked) =>
                                  handleAreaOfInterestChange(
                                    item.id,
                                    checked === true,
                                    item.name
                                  )
                                }
                              />
                              <Label htmlFor={`area-${item.id}`}>
                                {item.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem className="border-none" value="Roles">
                <AccordionTrigger
                  isChevron
                  className="text-sm justify-between w-full hover:bg-gray-50 rounded-md px-2 py-1 transition-colors"
                >
                  Job Role
                </AccordionTrigger>
                <AccordionContent>
                  <div className="max-h-[200px] overflow-y-auto w-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                    {/* Grouped Occupations: parent with children */}
                    {sortedOccupationParents.length > 0 ? (
                      sortedOccupationParents.map((parent) => (
                        <div key={parent.id} className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Label
                              htmlFor={`select-all-occupation-${parent.id}`}
                              className="text-sm font-medium"
                            >
                              {parent.name}
                            </Label>
                          </div>
                          <div className="flex flex-col gap-2 py-2 px-4">
                            {parent.subOccupations.map(
                              (item: { id: string; name: string }) => (
                                <div
                                  key={item.id}
                                  className="flex items-center gap-2"
                                >
                                  <Checkbox
                                    id={`occupation-${item.id}`}
                                    checked={selectedFilters.occupationIds.includes(
                                      item.id
                                    )}
                                    onCheckedChange={(checked) =>
                                      handleOccupationChange(
                                        item.id,
                                        checked === true
                                      )
                                    }
                                  />
                                  <Label htmlFor={`occupation-${item.id}`}>
                                    {item.name}
                                  </Label>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500 px-4 py-2">
                        No roles found.
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem className="border-none" value="Industry">
                <AccordionTrigger
                  isChevron
                  className="text-sm justify-between w-full hover:bg-gray-50 rounded-md px-2 py-1 transition-colors"
                >
                  Industry
                </AccordionTrigger>
                <AccordionContent>
                  <div className="max-h-[200px] overflow-y-auto w-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                    {/* Grouped Industries: parent with children */}
                    {sortedIndustryParents.length > 0 ? (
                      sortedIndustryParents.map((parent) => (
                        <div key={parent.id} className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Label
                              htmlFor={`select-all-industry-${parent.id}`}
                              className="text-sm font-medium"
                            >
                              {parent.name}
                            </Label>
                          </div>
                          <div className="flex flex-col gap-2 py-2 px-4">
                            {parent.subIndustries.map(
                              (item: { id: string; name: string }) => (
                                <div
                                  key={item.id}
                                  className="flex items-center gap-2"
                                >
                                  <Checkbox
                                    id={`industry-${item.id}`}
                                    checked={selectedFilters.industryIds.includes(
                                      item.id
                                    )}
                                    onCheckedChange={(checked) =>
                                      handleIndustryChange(
                                        item.id,
                                        checked === true
                                      )
                                    }
                                  />
                                  <Label htmlFor={`industry-${item.id}`}>
                                    {item.name}
                                  </Label>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500 px-4 py-2">
                        No industries found.
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </>
        )}
      </div>
    </aside>
  );
};
